import { useMemo } from 'react';
import { Lightbulb, Plus, DollarSign } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useBrand } from '@/config/brands';
import { useTheme } from '@/contexts/ThemeContext';
import { findSuggestions, BundleSuggestion } from '@/lib/procedureBundles';
import type { LocalScenarioProcedure } from '@/lib/scenarioStorage';

interface SmartSuggestionsProps {
  procedures: LocalScenarioProcedure[];
  onAddSuggestion: (suggestion: BundleSuggestion) => void;
}

export function SmartSuggestions({ procedures, onAddSuggestion }: SmartSuggestionsProps) {
  const brand = useBrand();
  const isAhp = brand.key === 'ahp';
  const { isDark } = useTheme();
  const ahpDark = isAhp && isDark;

  const suggestions = useMemo(() => {
    const existingCodes = new Set(procedures.map(p => p.code));
    const allSuggestions: BundleSuggestion[] = [];

    for (const proc of procedures) {
      const found = findSuggestions(proc.code, proc.description, existingCodes);
      for (const s of found) {
        if (!existingCodes.has(s.code) && !allSuggestions.some(x => x.code === s.code)) {
          allSuggestions.push(s);
        }
      }
    }

    // Limit to top 6 suggestions
    return allSuggestions.slice(0, 6);
  }, [procedures]);

  if (suggestions.length === 0) return null;

  return (
    <div className={`rounded-xl border p-3 ${
      ahpDark
        ? 'border-[rgba(197,165,90,0.2)] bg-[#0f1d2f]'
        : isAhp
        ? 'border-amber-200 bg-amber-50/50'
        : 'border-amber-500/20 bg-amber-500/5'
    }`}>
      <div className="flex items-center gap-2 mb-2">
        <Lightbulb className={`w-4 h-4 ${isAhp ? 'text-amber-600' : 'text-amber-400'}`} />
        <h4 className={`text-xs font-bold ${isAhp ? 'text-amber-800' : 'text-amber-400'}`}>
          Patients also need...
        </h4>
      </div>

      <div className="space-y-1.5">
        {suggestions.map((s) => (
          <div
            key={s.code}
            className={`flex items-center gap-2 p-2 rounded-lg transition-colors ${
              ahpDark
                ? 'bg-[#162a42] hover:bg-[#1a3050] border border-[rgba(197,165,90,0.15)]'
                : isAhp
                ? 'bg-white/80 hover:bg-white border border-amber-100'
                : 'bg-black/20 hover:bg-black/30 border border-amber-500/10'
            }`}
          >
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-1.5">
                <span className={`text-[9px] font-bold px-1 py-0.5 rounded ${
                  isAhp ? 'bg-gray-100 text-gray-600' : 'bg-white/5 text-muted-foreground'
                }`}>
                  {s.codeType}
                </span>
                <span className={`font-mono text-[10px] ${isAhp ? 'text-[#2b5e49]' : 'text-matrix-green'}`}>
                  {s.code}
                </span>
              </div>
              <p className="text-[11px] text-muted-foreground truncate mt-0.5">{s.description}</p>
              <p className={`text-[9px] mt-0.5 ${isAhp ? 'text-amber-700' : 'text-amber-400/80'}`}>
                {s.reason}
              </p>
            </div>

            <div className="flex items-center gap-2 shrink-0">
              <span className="text-[10px] font-mono text-muted-foreground">
                ${(s.estimatedCost * s.quantity).toLocaleString()}
              </span>
              <Button
                size="sm"
                variant="outline"
                onClick={() => onAddSuggestion(s)}
                className={`h-6 px-2 text-[10px] ${
                  isAhp
                    ? 'border-[#2b5e49]/30 text-[#2b5e49] hover:bg-[#2b5e49] hover:text-white'
                    : 'border-matrix-green/30 text-matrix-green hover:bg-matrix-green hover:text-black'
                }`}
              >
                <Plus className="w-3 h-3 mr-0.5" />
                Add
              </Button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
